// "Show password" for a field the user is SETTING rather than typing back.

import { el } from '../dom.js';

/**
 * A checkbox that flips `input` between `password` and `text`.
 *
 * Registration asks for the password ONCE — deliberately; a confirm field is a
 * second chance to make the same typo, and it is the thing people abandon a
 * signup over. But once is only safe if there is a way to read what was typed
 * before committing to it, because the alternative is discovering the typo at
 * the next login with no idea what was stored.
 *
 * A checkbox rather than an eye button inside the field: `label.check` is the
 * app's existing control for exactly this shape and already carries the reset
 * that stops the global `input { width: 100% }` from stretching a checkbox
 * across the row (CLAUDE.md, "A global `input` width lands on every radio").
 * An overlay button would need new positioning CSS on a form that is on the App
 * Store screenshots.
 *
 * Deliberately NOT given a `name`: it is a view control, not a field, and a
 * name would put `on` in the submitted form data.
 *
 * Off by default, and it never persists — revealing is a momentary check, not a
 * preference, and a remembered "show" would put someone's password on screen in
 * a coffee shop without their asking.
 */
export function passwordReveal(
  inputs: HTMLInputElement | HTMLInputElement[],
  label = 'Show password',
): HTMLElement {
  // A form with a "new" and a "confirm" field gets ONE checkbox over both, not
  // one each: the whole point of revealing is to compare them, and two switches
  // that must be flipped together is two chances to compare a visible string
  // against a row of dots and call them equal.
  const fields = Array.isArray(inputs) ? inputs : [inputs];
  const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
  box.addEventListener('change', () => {
    for (const input of fields) {
      // The browser moves the caret to the end when the type changes, so put it
      // back: someone who reveals mid-typing to check a character should be able
      // to keep going from where they were. Only the focused field has a caret
      // worth restoring.
      const focused = document.activeElement === input;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      input.type = box.checked ? 'text' : 'password';
      if (focused && start !== null && end !== null) {
        try {
          input.setSelectionRange(start, end);
        } catch {
          // Some browsers refuse setSelectionRange on certain input types. The
          // reveal itself still worked, and a caret at the end is cosmetic.
        }
      }
    }
  });
  return el('label', { class: 'check' }, [box, ` ${label}`]);
}
